import {createContext, Fragment, use, useEffect, useState, type ReactNode} from 'react'
import type {ChatState, PendingMessage} from '@microsoft/agent-host-protocol'
import {AlertIcon} from '@primer/octicons-react'
import {RelativeTime, Stack, VisuallyHidden} from '@primer/react'
import {LoadingMessage} from '@github-ui/agent-sessions-interface/components/LoadingMessage'
import {WorkingAsciiLoader} from '@github-ui/agent-sessions-interface/components/WorkingAsciiLoader'
import {
  getActivity,
  getQueuedMessages,
  getTurnError,
  getTurns,
  isWorking,
  needsInput,
  type TimelineTurn,
  type TimelineView,
  type SubagentHandle,
  type SubagentResolver,
} from '@github-ui/ahp-relay/ahp-session-selectors'
import {announce} from '@github-ui/aria-live'
import {parsePermissionCommand} from '@github-ui/one-copilot-composer/existing'
import type {RelaySteeringMessage} from '@github-ui/ahp-relay/relay-steering-messages'
import {getTaskStartupPresentation, type TaskStartup} from '../../../ahp/task-startup-store'
import {AhpTurnView} from './AhpTurnView'
import {
  AhpSubagentReference,
  type AhpSubagentComponent,
  type AhpSubagentTimelineProps,
} from './parts/AhpSubagentReference'
import {AhpPendingMessage} from './AhpPendingMessage'
import styles from '../EventSessionLogs.module.css'
import type {ModelForTokenUsage} from '@github-ui/copilot-chat/components/MessageTokenUsage'
import {
  EXTERNAL_OPTIMISTIC_TURN_ID,
  withOptimisticRecoveryTurn,
} from '@github-ui/copilot-shared-ui/hooks/use-optimistic-recovery-turns'
import type {ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'
import {SavedAhpHistoryProvider, useReadOnlyAhpHistory} from './AhpSessionContext'
import {projectSavedTimelineTurns} from '../../../utils/replay-ahp-events'
import {useSessionLoadingSource, type SessionLoadingSource} from '../../../hooks/use-session-loading-presentation'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'

const AvailableModelsContext = createContext<ModelForTokenUsage[] | undefined>(undefined)

/**
 * A pre-rendered notice interleaved with the timeline turns (e.g. a "Model
 * changed to …" row). The timeline stays agnostic about the content: consumers
 * supply the node and the anchor turn it should follow.
 */
export interface TimelineMarker {
  /** Stable id for React keys. */
  id: string
  /** Renders after the turn with this id; `null` renders before the first turn. Unknown ids render at the tail. */
  afterTurnId: string | null
  node: ReactNode
}

export interface AhpTimelineViewProps {
  /** The canonical AHP chat state, or undefined before the first snapshot. */
  state: ChatState | undefined
  savedState?: ChatState
  savedTurns?: readonly TimelineTurn[]
  SavedSubagentComponent?: AhpSubagentComponent
  resolveSavedSubagent?: SubagentResolver
  /** Resolved presentation state when `state` is absent (loading vs error). */
  view: TimelineView
  ariaLabel?: string
  errorMessage?: string
  additionalErrorMessage?: string
  /** Model-call failures keyed by turn id; the matching turn is decorated as errored. */
  modelFailureMessages?: Record<string, string>
  /** Client-side notices interleaved with turns (e.g. model/reasoning change rows). */
  timelineMarkers?: TimelineMarker[]
  /** Shown in place of "Loading…" while the session is still being provisioned; ignored once chat state arrives. */
  provisioningMessage?: string
  /** Phase-stable provisioning announcement (no clone percent), so screen readers announce phase transitions, not every percent. */
  provisioningAnnouncement?: string
  provisioning?: ProvisioningStatus
  availableModels?: ModelForTokenUsage[]
  /** When provided, queued messages render a control that cancels them by id. */
  onCancelQueuedMessage?: (id: string) => void | Promise<void>
  /** When provided, queued messages render a control that sends them immediately. */
  onSendNowQueuedMessage?: (message: PendingMessage) => void | Promise<void>
  /** Suppresses queued-message rows and announcements when the caller presents the queue elsewhere. */
  suppressQueuedMessages?: boolean
  /** Suppresses the transient thinking status when another status replaces it. */
  suppressProgressMessage?: boolean
  /** Creator-owned task startup whose first turn has not yet settled with the host. */
  taskStartup?: TaskStartup
  /** Normalized repository clone progress for a pending creator-owned task startup. */
  taskStartupCloneProgress?: number
  /** Page-local steering messages retained by the shared relay. */
  steeringMessages?: readonly RelaySteeringMessage[]
  /**
   * Suppresses the first turn's user-message bubble. Set for Managed CCA
   * sessions, where the kickoff seed is the model-facing task prompt already
   * shown by the friendly "started a task" initial message. Mirrors the legacy
   * non-AHP log view, which drops the sourceless kickoff for non-interactive
   * sessions.
   */
  suppressInitialUserMessage?: boolean
  /** Whether tool-spawned worker chats can be rendered and observed. */
  subagentChatsEnabled?: boolean
  SubagentComponent?: AhpSubagentComponent
  resolveSubagent?: SubagentResolver
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  loadingPresentation?: 'local' | 'page'
  loadingSourceKind?: SessionLoadingSource['kind']
}

function AhpSubagentTimeline(props: AhpSubagentTimelineProps) {
  return <AhpTimelineView {...props} />
}

function LiveAhpSubagent({subagent}: {subagent: SubagentHandle}) {
  return <AhpSubagentReference subagent={subagent} TimelineComponent={AhpSubagentTimeline} />
}

/**
 * Whether a turn is a tool-permission command.
 *
 * These are configuration, not conversation, and are no longer dispatched as
 * turns at all — the composer applies them and reports the result as a toast.
 * Sessions recorded before that change still hold them, so they stay hidden
 * here rather than resurfacing as chat.
 */
function isPermissionCommandTurn(turn: TimelineTurn): boolean {
  const command = parsePermissionCommand(turn.message?.text ?? '')
  return command?.kind === 'set' || command?.kind === 'show'
}

interface TimelineAnnouncement {
  assertive: boolean
  message: string
}

type TimelineRenderModel =
  | {kind: 'loading'; announcement: TimelineAnnouncement; message: string; turns: []}
  | {kind: 'error'; announcement: TimelineAnnouncement; message: string; turns: []}
  | {kind: 'empty'; announcement?: TimelineAnnouncement; turns: []; queuedMessages: []}
  | {
      kind: 'timeline'
      announcement?: TimelineAnnouncement
      progressMessage?: string
      turns: TimelineTurn[]
      queuedMessages: PendingMessage[]
    }

interface TurnErrorTracking {
  alertingKeys: Set<string>
  knownKeys: Set<string>
  signature: string
}

const EMPTY_TURN_ERROR_KEYS = new Set<string>()

function getErrorStatusMessage(view: TimelineView, errorMessage: string | undefined): string | undefined {
  return view === 'error' ? (errorMessage ?? 'Error loading session.') : undefined
}

function getPageLoadingState({
  enabled,
  sourceKind,
  timelineKind,
  hasPreparation,
  hasTaskStartupError,
  hasAdditionalError,
}: {
  enabled: boolean
  sourceKind: SessionLoadingSource['kind']
  timelineKind: TimelineRenderModel['kind']
  hasPreparation: boolean
  hasTaskStartupError: boolean
  hasAdditionalError: boolean
}): {loading: boolean; preparation: boolean; source: SessionLoadingSource} {
  const preparation = enabled && hasPreparation
  const loading = preparation || (enabled && timelineKind === 'loading' && !hasAdditionalError)
  const state: SessionLoadingSource = hasTaskStartupError
    ? {kind: sourceKind, state: 'error', replacesLoading: true}
    : loading
      ? {kind: sourceKind, state: 'pending'}
      : timelineKind === 'error' || hasAdditionalError
        ? {kind: sourceKind, state: 'error', replacesLoading: false}
        : {kind: sourceKind, state: 'idle'}

  return {
    loading,
    preparation,
    source: preparation
      ? state.state === 'error'
        ? {kind: 'preparation', state: 'error', replacesLoading: state.replacesLoading}
        : {kind: 'preparation', state: state.state}
      : state,
  }
}

function getTurnErrorKey(turn: TimelineTurn, index: number): string | undefined {
  if (!('state' in turn) || turn.state !== 'error') return
  const error = getTurnError(turn)
  if (!error?.message) return

  return `${turn.id ?? index}:${error.message}`
}

/** Response-part kinds that count as a real assistant answer (vs. `reasoning` or a `systemNotification`). */
const OUTPUT_PART_KINDS = new Set<string>(['markdown', 'contentRef', 'toolCall'])

function turnProducedOutput(turn: TimelineTurn): boolean {
  return turn.responseParts.some(part => OUTPUT_PART_KINDS.has(part.kind))
}

/**
 * Decorate any turn carrying a `_meta` model-call failure as errored. The host
 * doesn't end the turn on such a failure (the agent loop often retries), so a
 * turn that produced real output has recovered and is left untouched; only a
 * still-empty, non-`chat/error` turn is decorated.
 */
function applyModelFailures(
  turns: TimelineTurn[],
  modelFailureMessages: Record<string, string> | undefined,
): TimelineTurn[] {
  if (!modelFailureMessages) return turns
  return turns.map(turn => {
    const message = turn.id ? modelFailureMessages[turn.id] : undefined
    if (!message) return turn
    if ('state' in turn && turn.state === 'error') return turn
    if (turnProducedOutput(turn)) return turn
    return {
      ...turn,
      state: 'error',
      responseParts: [...turn.responseParts, {kind: 'error', error: {errorType: 'model_call_failure', message}}],
    } as TimelineTurn
  })
}

function getAnnouncementMessage(model: TimelineRenderModel): string | undefined {
  return model.announcement?.message
}

function getAnnouncementAssertive(model: TimelineRenderModel): boolean {
  return model.announcement?.assertive ?? false
}

function deriveTimelineRenderModel({
  state,
  view,
  errorMessage,
  modelFailureMessages,
  provisioningMessage,
  provisioningAnnouncement,
  suppressProgressMessage,
  turns,
  readOnlyHistory,
}: Pick<
  AhpTimelineViewProps,
  | 'state'
  | 'view'
  | 'errorMessage'
  | 'modelFailureMessages'
  | 'provisioningMessage'
  | 'provisioningAnnouncement'
  | 'suppressProgressMessage'
> & {turns?: TimelineTurn[]; readOnlyHistory?: boolean}): TimelineRenderModel {
  const errorStatusMessage = getErrorStatusMessage(view, errorMessage)

  if (errorStatusMessage) {
    return {
      kind: 'error',
      announcement: {assertive: true, message: errorStatusMessage},
      message: errorStatusMessage,
      turns: [],
    }
  }

  if (!state) {
    if (view === 'ready') {
      return {
        kind: 'empty',
        turns: [],
        queuedMessages: [],
      }
    }

    const loadingMessage = provisioningMessage ?? 'Loading…'
    // Phase-stable so a running clone doesn't re-announce on every percent change.
    const announcementMessage = provisioningAnnouncement ?? loadingMessage
    return {
      kind: 'loading',
      announcement: {assertive: false, message: announcementMessage},
      message: loadingMessage,
      turns: [],
    }
  }

  const renderedTurns = applyModelFailures(turns ?? getTurns(state), modelFailureMessages)
  const working = !readOnlyHistory && isWorking(state)
  const waitingForInput = !readOnlyHistory && needsInput(state)
  const queuedMessages = getQueuedMessages(state)

  if (renderedTurns.length === 0 && !working && queuedMessages.length === 0) {
    return {kind: 'empty', turns: [], queuedMessages: []}
  }

  const progressMessage =
    working && !waitingForInput && !suppressProgressMessage ? getActivity(state) || 'Thinking…' : undefined
  const announcementMessage = waitingForInput ? 'Waiting for confirmation' : progressMessage
  const announcement = announcementMessage ? {assertive: false, message: announcementMessage} : undefined

  return {kind: 'timeline', announcement, progressMessage, turns: renderedTurns, queuedMessages}
}

/**
 * Returns only the turn errors that became visible after the first real AHP snapshot.
 *
 * Terminal turn errors are rendered inline with the turn, so their alert semantics
 * need to be local to that turn instead of a single parent live announcement. The
 * first loaded snapshot can include historical failed turns, which should remain
 * readable but should not all announce at once on page load. After that baseline,
 * any newly appearing errored turn key is safe to expose as `role="alert"`.
 */
function useNewTurnErrorKeys(state: ChatState | undefined, turns: TimelineTurn[]): Set<string> {
  const turnErrorKeys = turns.map(getTurnErrorKey)
  const currentTurnErrorKeys = turnErrorKeys.filter((key): key is string => !!key)
  const turnErrorSignature = currentTurnErrorKeys.join('\0')
  const [turnErrorTracking, setTurnErrorTracking] = useState<TurnErrorTracking>()

  if (!state && turnErrorTracking) {
    setTurnErrorTracking(undefined)
  } else if (state && (!turnErrorTracking || turnErrorTracking.signature !== turnErrorSignature)) {
    const alertingKeys = new Set<string>()
    if (turnErrorTracking) {
      for (const turnErrorKey of currentTurnErrorKeys) {
        if (!turnErrorTracking.knownKeys.has(turnErrorKey)) {
          alertingKeys.add(turnErrorKey)
        }
      }
    }
    setTurnErrorTracking({
      alertingKeys,
      knownKeys: new Set(currentTurnErrorKeys),
      signature: turnErrorSignature,
    })
  }

  return turnErrorTracking?.alertingKeys ?? EMPTY_TURN_ERROR_KEYS
}

const EMPTY_QUEUED_IDS = new Set<string>()
const EMPTY_QUEUED_MESSAGES: PendingMessage[] = []
const EMPTY_TIMELINE_MARKERS: TimelineMarker[] = []
const EMPTY_STEERING_MESSAGES: readonly RelaySteeringMessage[] = []

interface GroupedTimelineMarkers {
  /** Notices anchored before the first turn (change happened on an empty timeline). */
  top: TimelineMarker[]
  /** Notices keyed by the turn id they render after. */
  byTurnId: Map<string, TimelineMarker[]>
  /** Notices whose anchor turn is no longer present; rendered after the last turn. */
  tail: TimelineMarker[]
}

/** Buckets markers into top / after-turn / tail groups so they interleave with the rendered turns. */
function groupTimelineMarkers(markers: TimelineMarker[], turns: TimelineTurn[]): GroupedTimelineMarkers {
  const knownTurnIds = new Set(turns.map(turn => turn.id).filter((id): id is string => !!id))
  const grouped: GroupedTimelineMarkers = {top: [], byTurnId: new Map(), tail: []}
  for (const marker of markers) {
    if (marker.afterTurnId === null) {
      grouped.top.push(marker)
    } else if (knownTurnIds.has(marker.afterTurnId)) {
      const existing = grouped.byTurnId.get(marker.afterTurnId)
      if (existing) {
        existing.push(marker)
      } else {
        grouped.byTurnId.set(marker.afterTurnId, [marker])
      }
    } else {
      grouped.tail.push(marker)
    }
  }
  return grouped
}

/**
 * Returns queued-message ids that appeared after the first snapshot, so a
 * status announcement fires when the user's message is queued but not for the
 * queue entries already present when the timeline first loaded.
 */
function useNewlyQueuedMessageIds(state: ChatState | undefined, queuedMessages: PendingMessage[]): Set<string> {
  const ids = queuedMessages.map(message => message.id)
  const signature = ids.join('\0')
  const [tracking, setTracking] = useState<{newIds: Set<string>; knownIds: Set<string>; signature: string}>()

  if (!state && tracking) {
    setTracking(undefined)
  } else if (state && (!tracking || tracking.signature !== signature)) {
    const newIds = new Set<string>()
    if (tracking) {
      for (const id of ids) {
        if (!tracking.knownIds.has(id)) newIds.add(id)
      }
    }
    setTracking({newIds, knownIds: new Set(ids), signature})
  }

  return tracking?.newIds ?? EMPTY_QUEUED_IDS
}

interface SteeringAnnouncementTracking {
  announcement?: string
  knownStatuses: Map<string, RelaySteeringMessage['status']>
  sequence: number
  signature: string
}

function useSteeringAnnouncement(
  state: ChatState | undefined,
  steeringMessages: readonly RelaySteeringMessage[],
): Pick<SteeringAnnouncementTracking, 'announcement' | 'sequence'> | undefined {
  const signature = steeringMessages.map(message => `${message.id}:${message.status}`).join('\0')
  const [tracking, setTracking] = useState<SteeringAnnouncementTracking>()

  if (!state && tracking) {
    setTracking(undefined)
  } else if (state && (!tracking || tracking.signature !== signature)) {
    let submittedCount = 0
    let handedOffCount = 0
    const knownStatuses = new Map(steeringMessages.map(message => [message.id, message.status]))

    if (tracking) {
      for (const message of steeringMessages) {
        const priorStatus = tracking.knownStatuses.get(message.id)
        if (!priorStatus) submittedCount++
        if (priorStatus === 'pending' && message.status === 'handed-off') handedOffCount++
      }
    }

    const announcements = []
    if (submittedCount > 0) {
      announcements.push(submittedCount === 1 ? 'Steering message sent' : `${submittedCount} steering messages sent`)
    }
    if (handedOffCount > 0) {
      announcements.push(
        handedOffCount === 1
          ? 'Steering message received by agent'
          : `${handedOffCount} steering messages received by agent`,
      )
    }
    setTracking({
      ...(announcements.length > 0 ? {announcement: announcements.join('. ')} : {}),
      knownStatuses,
      sequence: (tracking?.sequence ?? 0) + (announcements.length > 0 ? 1 : 0),
      signature,
    })
  }

  return tracking
}

function getTurnKey(turn: TimelineTurn, index: number): string {
  return turn.id ?? `turn-${index}`
}

function groupSteeringMessagesByTurn(
  turns: TimelineTurn[],
  steeringMessages: readonly RelaySteeringMessage[],
): ReadonlyMap<string, readonly RelaySteeringMessage[]> {
  const messagesByTurn = new Map<string, RelaySteeringMessage[]>()
  const lastTurn = turns.at(-1)
  const fallbackTurnKey = lastTurn ? getTurnKey(lastTurn, turns.length - 1) : undefined

  for (const message of steeringMessages) {
    const turnKey = message.turnId ?? fallbackTurnKey
    if (!turnKey) continue
    const messages = messagesByTurn.get(turnKey)
    if (messages) {
      messages.push(message)
    } else {
      messagesByTurn.set(turnKey, [message])
    }
  }

  return messagesByTurn
}

/**
 * Pure presentation of an AHP session timeline. Renders directly from
 * `ChatState`, so any consumer that can produce one (the agent-sessions relay
 * registry or quick-chat's direct runtime) can render the same timeline without
 * depending on how the state was acquired.
 */
export function AhpTimelineView({
  state,
  savedState,
  savedTurns,
  SavedSubagentComponent,
  resolveSavedSubagent,
  view,
  ariaLabel = 'Session timeline',
  errorMessage,
  additionalErrorMessage,
  modelFailureMessages,
  timelineMarkers = EMPTY_TIMELINE_MARKERS,
  provisioningMessage,
  provisioningAnnouncement,
  provisioning,
  suppressProgressMessage = false,
  taskStartup,
  taskStartupCloneProgress,
  availableModels: providedModels,
  onCancelQueuedMessage,
  onSendNowQueuedMessage,
  suppressQueuedMessages = false,
  steeringMessages = EMPTY_STEERING_MESSAGES,
  suppressInitialUserMessage = false,
  subagentChatsEnabled = true,
  SubagentComponent = LiveAhpSubagent,
  resolveSubagent,
  onContributedInstructions,
  loadingPresentation = 'local',
  loadingSourceKind = 'history',
}: AhpTimelineViewProps) {
  const inheritedModels = use(AvailableModelsContext)
  const availableModels = providedModels ?? inheritedModels
  const readOnlyHistory = useReadOnlyAhpHistory()
  const canonicalState = withOptimisticRecoveryTurn(state, taskStartup?.prompt, EXTERNAL_OPTIMISTIC_TURN_ID, true)
  const matchingSavedState =
    !canonicalState || canonicalState.resource === savedState?.resource ? savedState : undefined
  const liveTurns = canonicalState ? getTurns(canonicalState) : []
  const liveTurnIds = new Set(liveTurns.map(turn => turn.id))
  // Live state owns the queue; saved-only entries may already have been consumed or removed.
  const displayState = canonicalState ?? matchingSavedState
  const turnStartedAt = canonicalState?.activeTurn?.startedAt
  const displayTurns = projectSavedTimelineTurns(
    matchingSavedState ? (savedTurns ?? getTurns(matchingSavedState)) : [],
    liveTurns,
  )
  const externalOptimisticTurnVisible = canonicalState?.activeTurn?.id === EXTERNAL_OPTIMISTIC_TURN_ID
  const model = deriveTimelineRenderModel({
    state: displayState,
    turns: displayTurns,
    readOnlyHistory: readOnlyHistory || !canonicalState,
    view: matchingSavedState ? 'ready' : view,
    errorMessage,
    modelFailureMessages,
    provisioningMessage,
    provisioningAnnouncement,
    suppressProgressMessage: suppressProgressMessage || (!!taskStartup && externalOptimisticTurnVisible),
  })
  const newTurnErrorKeys = useNewTurnErrorKeys(canonicalState, model.turns)
  const queuedMessages =
    !suppressQueuedMessages && model.kind === 'timeline' ? model.queuedMessages : EMPTY_QUEUED_MESSAGES
  const newlyQueuedIds = useNewlyQueuedMessageIds(canonicalState, queuedMessages)
  const announcementMessage = getAnnouncementMessage(model)
  const announcementAssertive = getAnnouncementAssertive(model)
  const steeringAnnouncement = useSteeringAnnouncement(canonicalState, steeringMessages)
  const steeringMessagesByTurn = groupSteeringMessagesByTurn(model.turns, steeringMessages)
  const showTaskStartupPhase = taskStartup?.status === 'pending' && externalOptimisticTurnVisible
  const taskStartupPresentation = showTaskStartupPhase
    ? getTaskStartupPresentation(taskStartup, taskStartupCloneProgress)
    : undefined
  const taskStartupError = taskStartup?.status === 'failed' ? taskStartup.error : undefined
  const pageLoadingState = getPageLoadingState({
    enabled: loadingPresentation === 'page',
    sourceKind: loadingSourceKind,
    timelineKind: model.kind,
    hasPreparation: Boolean(
      taskStartupPresentation ||
      (!state && (provisioningMessage || provisioning?.phase === 'preparing' || provisioning?.phase === 'cloning')),
    ),
    hasTaskStartupError: Boolean(taskStartupError),
    hasAdditionalError: Boolean(additionalErrorMessage),
  })
  const preparationPhase = taskStartup ? (showTaskStartupPhase ? taskStartup.phase : undefined) : provisioning?.phase
  useSessionLoadingSource(
    pageLoadingState.source.kind === 'preparation' &&
      (preparationPhase === 'preparing' || preparationPhase === 'cloning')
      ? {
          ...pageLoadingState.source,
          phase: preparationPhase,
          progress: taskStartup ? taskStartupCloneProgress : provisioning?.progress,
        }
      : pageLoadingState.source.kind === 'preparation' &&
          taskStartup?.status === 'pending' &&
          taskStartup.phase === 'connecting'
        ? {...pageLoadingState.source, launchKind: taskStartup.launch?.kind}
        : pageLoadingState.source,
    {enabled: loadingPresentation === 'page'},
  )
  const visibleAnnouncementMessage = taskStartupError
    ? taskStartupError
    : pageLoadingState.loading
      ? undefined
      : taskStartupPresentation?.message
        ? taskStartupPresentation.message
        : announcementMessage
  const visibleAnnouncementAssertive = taskStartupError ? true : showTaskStartupPhase ? false : announcementAssertive
  const visibleErrorMessage = taskStartupError ? undefined : additionalErrorMessage

  useEffect(() => {
    if (!visibleAnnouncementMessage) return

    // These announcements will be for errors that aren't associated with a particular conversation turn
    announce(visibleAnnouncementMessage, {assertive: visibleAnnouncementAssertive})
  }, [visibleAnnouncementAssertive, visibleAnnouncementMessage])

  useEffect(() => {
    if (newlyQueuedIds.size === 0) return
    announce(newlyQueuedIds.size === 1 ? 'Message queued' : `${newlyQueuedIds.size} messages queued`, {
      assertive: false,
    })
  }, [newlyQueuedIds])

  useEffect(() => {
    if (steeringAnnouncement?.announcement) announce(steeringAnnouncement.announcement, {assertive: false})
  }, [steeringAnnouncement?.announcement, steeringAnnouncement?.sequence])

  useEffect(() => {
    if (!visibleErrorMessage) return

    announce(visibleErrorMessage, {assertive: true})
  }, [visibleErrorMessage])

  const groupedMarkers = groupTimelineMarkers(timelineMarkers, model.turns)
  const renderMarkers = (markers: TimelineMarker[]) =>
    markers.map(marker => (
      <li key={marker.id} className={styles.timelineMarker} data-testid="timeline-marker">
        {marker.node}
      </li>
    ))

  return (
    <div data-testid="ahp-session-timeline" tabIndex={-1} className={styles.container}>
      {visibleErrorMessage && <div className={styles.errorBanner}>{visibleErrorMessage}</div>}
      {taskStartupError && (
        <div className={styles.errorBanner}>
          <AlertIcon size={16} className={styles.errorIcon} aria-hidden="true" />
          <span>{taskStartupError}</span>
        </div>
      )}
      {model.kind === 'loading' && loadingPresentation === 'local' && <LoadingMessage message={model.message} />}
      {model.kind === 'error' && (
        <div className={styles.errorBanner}>
          <AlertIcon size={16} className={styles.errorIcon} aria-hidden="true" />
          <span>{model.message}</span>
        </div>
      )}
      {/* Pending model-change notices still render so a change made before the first turn is shown. */}
      {model.kind === 'empty' && timelineMarkers.length > 0 && (
        <ol aria-label={ariaLabel} className={styles.container}>
          {renderMarkers(timelineMarkers)}
        </ol>
      )}
      {model.kind === 'timeline' && (
        <>
          <ol aria-label={ariaLabel} className={styles.container}>
            {renderMarkers(groupedMarkers.top)}
            {model.turns.map((turn, index) => {
              const afterTurnMarkers = turn.id ? groupedMarkers.byTurnId.get(turn.id) : undefined
              // Notices anchored to a hidden permission turn still interleave in
              // chronological order, so the turn's slot is kept, just not its body.
              if (isPermissionCommandTurn(turn)) {
                return (
                  <Fragment key={turn.id ?? `turn-${index}`}>
                    {afterTurnMarkers && renderMarkers(afterTurnMarkers)}
                  </Fragment>
                )
              }

              const turnErrorKey = getTurnErrorKey(turn, index)
              const turnKey = getTurnKey(turn, index)

              return (
                <Fragment key={turnKey}>
                  <AvailableModelsContext value={availableModels}>
                    <SavedAhpHistoryProvider saved={!liveTurnIds.has(turn.id)}>
                      <AhpTurnView
                        turn={turn}
                        chatResource={displayState?.resource}
                        announceError={!!turnErrorKey && newTurnErrorKeys.has(turnErrorKey)}
                        availableModels={availableModels}
                        steeringMessages={steeringMessagesByTurn.get(turnKey)}
                        suppressUserMessage={suppressInitialUserMessage && index === 0}
                        SubagentComponent={
                          !liveTurnIds.has(turn.id) && SavedSubagentComponent
                            ? SavedSubagentComponent
                            : SubagentComponent
                        }
                        subagentOffline={!liveTurnIds.has(turn.id) && !!SavedSubagentComponent}
                        subagentChatsEnabled={subagentChatsEnabled}
                        {...(onContributedInstructions ? {onContributedInstructions} : {})}
                        resolveSubagent={
                          !liveTurnIds.has(turn.id) ? (resolveSavedSubagent ?? resolveSubagent) : resolveSubagent
                        }
                      />
                    </SavedAhpHistoryProvider>
                  </AvailableModelsContext>
                  {afterTurnMarkers && renderMarkers(afterTurnMarkers)}
                </Fragment>
              )
            })}
            {renderMarkers(groupedMarkers.tail)}
          </ol>
          {taskStartupPresentation && loadingPresentation === 'local' ? (
            <LoadingMessage message={taskStartupPresentation.message} progress={taskStartupPresentation.progress} />
          ) : (
            !pageLoadingState.preparation &&
            model.progressMessage &&
            (turnStartedAt && Number.isFinite(Date.parse(turnStartedAt)) ? (
              <Stack direction="horizontal" align="center" gap="condensed" className={styles.WorkingTurn}>
                <WorkingAsciiLoader />
                <span role="timer" aria-label="Time elapsed in this turn" aria-live="off">
                  <RelativeTime datetime={turnStartedAt} format="elapsed" noTitle />
                </span>
                <VisuallyHidden>{model.progressMessage}</VisuallyHidden>
              </Stack>
            ) : (
              <div className={styles.WorkingMessage}>
                <LoadingMessage message={model.progressMessage} />
              </div>
            ))
          )}
          {!suppressQueuedMessages && model.queuedMessages.length > 0 && (
            <SavedAhpHistoryProvider saved={!canonicalState}>
              <ol aria-label="Queued messages" className={styles.container}>
                {model.queuedMessages.map(message => (
                  <AhpPendingMessage
                    key={message.id}
                    message={message}
                    {...(onCancelQueuedMessage ? {onCancel: onCancelQueuedMessage} : {})}
                    {...(onSendNowQueuedMessage ? {onSendNow: onSendNowQueuedMessage} : {})}
                  />
                ))}
              </ol>
            </SavedAhpHistoryProvider>
          )}
        </>
      )}
    </div>
  )
}
